import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import UserProfile from '../index';
import {
  DependentView,
  DependentRelationship,
  InsuranceStatus,
  CreateDependentInput,
  UpdateDependentInput,
  DependentDocumentView,
  DependentDocumentType,
  getMyDependents,
  getEmployeeDependents,
  createDependent,
  updateDependent,
  archiveDependent,
  getDependentDocuments,
  uploadDependentDocument,
  deleteDependentDocument,
  getDependentDocumentUrl,
} from '../../../api/General/dependents';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import StatusBadge from '../../../components/ui/StatusBadge';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { DatePicker as CustomDatePicker, formatDateForInput } from '../../../components/dateTimePickers';

interface Props {
  role: string;
  userId: string;
  selectedUser?: { id: string; fullName?: string };
  selectedUserManagers?: { id: string }[];
}

const relationshipLabels: Record<DependentRelationship, string> = {
  [DependentRelationship.Spouse]: "Spouse",
  [DependentRelationship.Child]: "Child",
  [DependentRelationship.Parent]: "Parent",
  [DependentRelationship.Sibling]: "Sibling",
  [DependentRelationship.Other]: "Other",
};

const insuranceStatusLabels: Record<InsuranceStatus, string> = {
  [InsuranceStatus.Pending]: "Pending",
  [InsuranceStatus.Active]: "Active",
  [InsuranceStatus.Inactive]: "Inactive",
};

const insuranceStatusVariant: Record<InsuranceStatus, 'warning' | 'success' | 'error'> = {
  [InsuranceStatus.Pending]: 'warning',
  [InsuranceStatus.Active]: 'success',
  [InsuranceStatus.Inactive]: 'error',
};

const documentTypeLabels: Record<DependentDocumentType, string> = {
  [DependentDocumentType.BirthCertificate]: "BirthCertificate",
  [DependentDocumentType.Passport]: "Passport",
  [DependentDocumentType.InsuranceCard]: "InsuranceCard",
  [DependentDocumentType.MedicalCertificate]: "MedicalCertificate",
  [DependentDocumentType.Other]: "Other",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const emptyForm: CreateDependentInput = {
  fullName: "",
  dateOfBirth: "",
  relationship: DependentRelationship.Child,
  gender: "",
  nationality: "",
  nationalId: "",
  notes: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  medicalConditions: [],
};

const ProfileDependents: React.FC<Props> = ({ userId, role }) => {
  const { t } = useTranslation();
  const { id: profileId } = useParams<{ id: string }>();
  const isOwnProfile = !profileId || profileId === userId;
  const canEdit = isOwnProfile || role === "Hr" || role === "Admin";

  const [dependents, setDependents] = useState<DependentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState<DependentView | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<CreateDependentInput>({ ...emptyForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [medicalInput, setMedicalInput] = useState("");
  const { toast, showToast, dismissToast } = useToast();
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Document state
  const [documents, setDocuments] = useState<DependentDocumentView[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<DependentDocumentType>(DependentDocumentType.Other);
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const targetId = profileId || userId;
      const result = isOwnProfile
        ? await getMyDependents()
        : await getEmployeeDependents(targetId);

      if (result.errors.length === 0) {
        setDependents(result.payload);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [profileId, userId, isOwnProfile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setEditMode(false);
    setMedicalInput("");
    setFormOpen(true);
  };

  const openEditForm = (dep: DependentView) => {
    setForm({
      fullName: dep.fullName,
      dateOfBirth: dep.dateOfBirth.split("T")[0],
      relationship: dep.relationship,
      gender: dep.gender || "",
      nationality: dep.nationality || "",
      nationalId: dep.nationalId || "",
      notes: dep.notes || "",
      emergencyContactName: dep.emergencyContactName || "",
      emergencyContactPhone: dep.emergencyContactPhone || "",
      emergencyContactRelationship: dep.emergencyContactRelationship || "",
      medicalConditions: [...dep.medicalConditions],
    });
    setEditId(dep.id);
    setEditMode(true);
    setMedicalInput("");
    setFormOpen(true);
    setDetailsOpen(false);
  };

  const handleSubmit = useCallback(async () => {
    if (!form.fullName.trim() || !form.dateOfBirth) {
      showToast(t("dependents.fillRequired"), "error");
      return;
    }

    setSubmitting(true);
    try {
      // When HR/Admin adds a dependent from another employee's profile, attach that
      // employee's id so the record is owned by them — not by the acting HR user.
      const targetId = profileId || userId;
      const result = editMode && editId
        ? await updateDependent({ ...form, id: editId } as UpdateDependentInput)
        : await createDependent(isOwnProfile ? form : { ...form, employeeId: targetId });

      if (result.errors.length === 0) {
        showToast(editMode ? t("dependents.updated") : t("dependents.created"), "success");
        setFormOpen(false);
        loadData();
      } else {
        showToast(result.errors[0]?.message || t("common.error"), "error");
      }
    } catch {
      showToast(t("common.error"), "error");
    } finally {
      setSubmitting(false);
    }
  }, [form, editMode, editId, isOwnProfile, profileId, userId, loadData, showToast, t]);

  const handleDelete = useCallback(async (id: number) => {
    try {
      const result = await archiveDependent(id);
      if (result.errors.length === 0) {
        showToast(t("dependents.removed"), "success");
        setDeleteConfirm(null);
        setDetailsOpen(false);
        loadData();
      } else {
        showToast(result.errors[0]?.message || t("common.error"), "error");
      }
    } catch {
      showToast(t("common.error"), "error");
    }
  }, [loadData, showToast, t]);

  const addMedicalCondition = () => {
    if (medicalInput.trim()) {
      setForm(prev => ({
        ...prev,
        medicalConditions: [...(prev.medicalConditions || []), medicalInput.trim()],
      }));
      setMedicalInput("");
    }
  };

  const removeMedicalCondition = (idx: number) => {
    setForm(prev => ({
      ...prev,
      medicalConditions: (prev.medicalConditions || []).filter((_, i) => i !== idx),
    }));
  };

  const openDetails = (dep: DependentView) => {
    setSelectedDependent(dep);
    setDetailsOpen(true);
    loadDocuments(dep.id);
  };

  const loadDocuments = useCallback(async (dependentId: number) => {
    setDocsLoading(true);
    try {
      const result = await getDependentDocuments(dependentId);
      if (result.errors.length === 0) {
        setDocuments(result.payload);
      }
    } catch {
      // silent
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const handleUploadDocument = useCallback(async () => {
    if (!uploadFile || !selectedDependent) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(uploadFile.type)) {
      showToast(t("dependents.docs.invalidType"), "error");
      return;
    }

    setUploading(true);
    try {
      const result = await uploadDependentDocument({
        dependentId: selectedDependent.id,
        documentType: uploadDocType,
        description: uploadDescription || undefined,
        file: uploadFile,
      });

      if (result.errors.length === 0) {
        showToast(t("dependents.docs.uploaded"), "success");
        setUploadOpen(false);
        setUploadFile(null);
        setUploadDocType(DependentDocumentType.Other);
        setUploadDescription("");
        loadDocuments(selectedDependent.id);
      } else {
        showToast(result.errors[0]?.message || t("common.error"), "error");
      }
    } catch {
      showToast(t("common.error"), "error");
    } finally {
      setUploading(false);
    }
  }, [uploadFile, selectedDependent, uploadDocType, uploadDescription, loadDocuments, showToast, t]);

  const handleDeleteDocument = useCallback(async (docId: number) => {
    try {
      const result = await deleteDependentDocument(docId);
      if (result.errors.length === 0) {
        showToast(t("dependents.docs.deleted"), "success");
        setDeleteDocConfirm(null);
        if (selectedDependent) loadDocuments(selectedDependent.id);
      } else {
        showToast(result.errors[0]?.message || t("common.error"), "error");
      }
    } catch {
      showToast(t("common.error"), "error");
    }
  }, [selectedDependent, loadDocuments, showToast, t]);

  const handleViewDocument = useCallback(async (docId: number) => {
    try {
      const result = await getDependentDocumentUrl(docId);
      if (result.errors.length === 0 && result.payload) {
        window.open(result.payload, "_blank");
      } else {
        showToast(t("common.error"), "error");
      }
    } catch {
      showToast(t("common.error"), "error");
    }
  }, [showToast, t]);

  return (
    <UserProfile>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t("dependents.title")}</h2>
            <p className="text-sm text-muted-foreground">{t("dependents.subtitle")}</p>
          </div>
          {canEdit && (
            <Button
              onClick={openAddForm}
              iconName="Plus"
              data-testid="add-dependent-btn"
            >
              {t("dependents.addDependent")}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : dependents.length === 0 ? (
          <div className="text-center py-12 px-4 bg-card border border-border rounded-lg">
            <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-foreground">{t("dependents.noDependents")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("dependents.noDependentsDescription")}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden hidden sm:block">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t("dependents.name")}</th>
                    <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t("dependents.relationship")}</th>
                    <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t("dependents.age")}</th>
                    <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t("dependents.insuranceStatus")}</th>
                    <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t("dependents.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dependents.map((dep) => (
                    <tr key={dep.id} className="hover:bg-muted">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{dep.fullName}</p>
                            <p className="text-xs text-muted-foreground">{dep.gender || "\u2014"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {t(`dependents.rel${relationshipLabels[dep.relationship]}`)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {dep.age} {t("dependents.years")}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge variant={insuranceStatusVariant[dep.insuranceStatus]} size="sm">
                          {t(`dependents.insurance${insuranceStatusLabels[dep.insuranceStatus]}`)}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openDetails(dep)}
                            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            title={t("common.view")}
                            data-testid={`view-dependent-${dep.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {canEdit && (
                            <>
                              <button
                                onClick={() => openEditForm(dep)}
                                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-primary hover:text-primary rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                title={t("common.edit")}
                                data-testid={`edit-dependent-${dep.id}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(dep.id)}
                                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-destructive hover:text-destructive rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                title={t("common.remove")}
                                data-testid={`remove-dependent-${dep.id}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card fallback */}
            <div className="space-y-3 sm:hidden">
              {dependents.map((dep) => (
                <div key={dep.id} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{dep.fullName}</p>
                      <p className="text-xs text-muted-foreground">{t(`dependents.rel${relationshipLabels[dep.relationship]}`)}</p>
                    </div>
                    <StatusBadge variant={insuranceStatusVariant[dep.insuranceStatus]} size="sm">
                      {t(`dependents.insurance${insuranceStatusLabels[dep.insuranceStatus]}`)}
                    </StatusBadge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{dep.age} {t("dependents.years")} &middot; {dep.gender || "\u2014"}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openDetails(dep)} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg" title={t("common.view")}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      </button>
                      {canEdit && (
                        <>
                          <button onClick={() => openEditForm(dep)} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-primary rounded-lg" title={t("common.edit")}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => setDeleteConfirm(dep.id)} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-destructive rounded-lg" title={t("common.remove")}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent size="lg">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <DialogTitle>
                    {editMode ? t("dependents.editDependent") : t("dependents.addDependent")}
                  </DialogTitle>
                  <DialogDescription>{t("dependents.formDescription")}</DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("dependents.fullName")} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-fullname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("dependents.dateOfBirth")} <span className="text-destructive">*</span>
                  </label>
                  <CustomDatePicker
                    label=""
                    maxDate={new Date()}
                    value={form.dateOfBirth || null}
                    onChange={(d: any) => setForm(prev => ({ ...prev, dateOfBirth: d ? formatDateForInput(d) : '' }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.relationship")}</label>
                  <select
                    value={form.relationship}
                    onChange={(e) => setForm(prev => ({ ...prev, relationship: e.target.value as DependentRelationship }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-relationship"
                  >
                    {Object.entries(relationshipLabels).map(([val, label]) => (
                      <option key={val} value={val}>{t(`dependents.rel${label}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.gender")}</label>
                  <select
                    value={form.gender || ""}
                    onChange={(e) => setForm(prev => ({ ...prev, gender: e.target.value || undefined }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-gender"
                  >
                    <option value="">{t("common.select")}</option>
                    <option value="Male">{t("dependents.male")}</option>
                    <option value="Female">{t("dependents.female")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.nationality")}</label>
                  <input
                    type="text"
                    value={form.nationality || ""}
                    onChange={(e) => setForm(prev => ({ ...prev, nationality: e.target.value || undefined }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-nationality"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.nationalId")}</label>
                  <input
                    type="text"
                    value={form.nationalId || ""}
                    onChange={(e) => setForm(prev => ({ ...prev, nationalId: e.target.value || undefined }))}
                    placeholder={t("dependents.nationalIdPlaceholder")}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-nationalid"
                  />
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.emergencyContact")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.contactName")}</label>
                    <input
                      type="text"
                      value={form.emergencyContactName || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, emergencyContactName: e.target.value || undefined }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                      data-testid="dependent-ec-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.contactPhone")}</label>
                    <input
                      type="text"
                      value={form.emergencyContactPhone || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, emergencyContactPhone: e.target.value || undefined }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                      data-testid="dependent-ec-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.contactRelationship")}</label>
                    <input
                      type="text"
                      value={form.emergencyContactRelationship || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, emergencyContactRelationship: e.target.value || undefined }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                      data-testid="dependent-ec-relationship"
                    />
                  </div>
                </div>
              </div>

              {/* Medical Conditions */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.medicalConditions")}</h3>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={medicalInput}
                    onChange={(e) => setMedicalInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMedicalCondition())}
                    placeholder={t("dependents.addCondition")}
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="dependent-medical-input"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={addMedicalCondition}
                  >
                    {t("common.add")}
                  </Button>
                </div>
                {(form.medicalConditions || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(form.medicalConditions || []).map((condition, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm text-foreground">
                        {condition}
                        <button
                          type="button"
                          onClick={() => removeMedicalCondition(idx)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.notes")}</label>
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value || undefined }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none resize-none"
                  data-testid="dependent-notes"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setFormOpen(false)}
                data-testid="cancel-form-btn"
              >
                {t("Cancel")}
              </Button>
              <Button
                onClick={handleSubmit}
                loading={submitting}
                data-testid="submit-dependent-btn"
              >
                {submitting ? t("common.saving") : editMode ? t("common.save") : t("dependents.addDependent")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Details Modal */}
        <Dialog open={detailsOpen && !!selectedDependent} onOpenChange={(open) => { if (!open) setDetailsOpen(false); }}>
          {selectedDependent && (
            <DialogContent size="lg">
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <DialogTitle>{selectedDependent.fullName}</DialogTitle>
                    <DialogDescription>{t(`dependents.rel${relationshipLabels[selectedDependent.relationship]}`)}</DialogDescription>
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditForm(selectedDependent)}
                      data-testid="details-edit-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                  )}
                </div>
              </DialogHeader>
              <DialogBody className="space-y-6">
                {/* Personal Details */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.personalDetails")}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.dateOfBirth")}</p>
                      <p className="text-sm text-foreground">{new Date(selectedDependent.dateOfBirth).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.age")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.age} {t("dependents.years")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.gender")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.gender || "\u2014"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.nationality")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.nationality || "\u2014"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.nationalId")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.nationalId || "\u2014"}</p>
                    </div>
                  </div>
                </div>

                {/* Insurance */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.insuranceDetails")}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.insuranceStatus")}</p>
                      <StatusBadge variant={insuranceStatusVariant[selectedDependent.insuranceStatus]} size="sm">
                        {t(`dependents.insurance${insuranceStatusLabels[selectedDependent.insuranceStatus]}`)}
                      </StatusBadge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.provider")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.insuranceProvider || "\u2014"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.policyNumber")}</p>
                      <p className="text-sm text-foreground">{selectedDependent.insurancePolicyNumber || "\u2014"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("dependents.expiryDate")}</p>
                      <p className="text-sm text-foreground">
                        {selectedDependent.insuranceExpiryDate
                          ? new Date(selectedDependent.insuranceExpiryDate).toLocaleDateString()
                          : "\u2014"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                {(selectedDependent.emergencyContactName || selectedDependent.emergencyContactPhone) && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.emergencyContact")}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">{t("dependents.contactName")}</p>
                        <p className="text-sm text-foreground">{selectedDependent.emergencyContactName || "\u2014"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t("dependents.contactPhone")}</p>
                        <p className="text-sm text-foreground">{selectedDependent.emergencyContactPhone || "\u2014"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t("dependents.contactRelationship")}</p>
                        <p className="text-sm text-foreground">{selectedDependent.emergencyContactRelationship || "\u2014"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Medical */}
                {selectedDependent.medicalConditions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.medicalConditions")}</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDependent.medicalConditions.map((condition, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 bg-destructive/10 text-destructive rounded-md text-sm">
                          {condition}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedDependent.notes && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{t("dependents.notes")}</h3>
                    <p className="text-sm text-muted-foreground">{selectedDependent.notes}</p>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-foreground">{t("dependents.docs.title")}</h3>
                    {canEdit && (
                      <Button
                        size="xs"
                        onClick={() => setUploadOpen(true)}
                        iconName="Upload"
                        data-testid="upload-doc-btn"
                      >
                        {t("dependents.docs.upload")}
                      </Button>
                    )}
                  </div>

                  {docsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-6 border border-dashed border-border rounded-lg">
                      <svg className="mx-auto h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-2 text-xs text-muted-foreground">{t("dependents.docs.noDocuments")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="border border-border rounded-lg p-3 hover:border-primary/30 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 bg-primary/5 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4.5 h-4.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate" title={doc.fileName}>
                                {doc.fileName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {t(`dependents.docs.type${documentTypeLabels[doc.documentType]}`)} &middot; {formatFileSize(doc.fileSize)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border">
                            <button
                              onClick={() => handleViewDocument(doc.id)}
                              className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/5 rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              title={t("dependents.docs.view")}
                              data-testid={`view-doc-${doc.id}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleViewDocument(doc.id)}
                              className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/5 rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              title={t("dependents.docs.download")}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                            {canEdit && (
                              <button
                                onClick={() => setDeleteDocConfirm(doc.id)}
                                className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                title={t("common.remove")}
                                data-testid={`delete-doc-${doc.id}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogBody>
            </DialogContent>
          )}
        </Dialog>

        {/* Upload Document Modal */}
        <Dialog open={uploadOpen && !!selectedDependent} onOpenChange={(o) => { if (!o) { setUploadOpen(false); setUploadFile(null); } }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t("dependents.docs.uploadTitle")}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.docs.documentType")}</label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value as DependentDocumentType)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                  data-testid="doc-type-select"
                >
                  {Object.entries(documentTypeLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`dependents.docs.type${label}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.docs.description")}</label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder={t("dependents.docs.descriptionPlaceholder")}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                  data-testid="doc-description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("dependents.docs.file")}</label>
                {!uploadFile ? (
                  <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <svg className="w-6 h-6 text-muted-foreground mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <p className="text-xs text-muted-foreground">{t("dependents.docs.dropzone")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("dependents.docs.acceptedFormats")}</p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      data-testid="doc-file-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-center gap-3 p-3 border border-border rounded-lg bg-muted">
                    <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{uploadFile.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(uploadFile.size)}</p>
                    </div>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setUploadOpen(false); setUploadFile(null); }}>
                {t("Cancel")}
              </Button>
              <Button
                onClick={handleUploadDocument}
                disabled={!uploadFile || uploading}
                loading={uploading}
                data-testid="submit-upload-btn"
              >
                {uploading ? t("common.saving") : t("dependents.docs.upload")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Document Confirmation */}
        <Dialog open={deleteDocConfirm !== null} onOpenChange={(o) => { if (!o) setDeleteDocConfirm(null); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t("dependents.docs.confirmDelete")}</DialogTitle>
              <DialogDescription>{t("dependents.docs.confirmDeleteDescription")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDocConfirm(null)}>
                {t("Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteDocConfirm !== null && handleDeleteDocument(deleteDocConfirm)}
                data-testid="confirm-delete-doc-btn"
              >
                {t("common.remove")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dependent Confirmation */}
        <Dialog open={deleteConfirm !== null} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t("dependents.confirmRemove")}</DialogTitle>
              <DialogDescription>{t("dependents.confirmRemoveDescription")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                {t("Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
              >
                {t("common.remove")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toast */}
        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </UserProfile>
  );
};

const mapStateToProps = (state: { user: { role: string; userId: string; selectedUser: { id: string; fullName?: string }; selectedUserManagers: { id: string }[] } }) => ({
  role: state.user.role,
  userId: state.user.userId,
  selectedUser: state.user.selectedUser,
  selectedUserManagers: state.user.selectedUserManagers,
});

export default connect(mapStateToProps)(ProfileDependents);
